import { Add, Delete, SearchOff } from "@mui/icons-material";
import CachedIcon from "@mui/icons-material/Cached";
import FilterListIcon from "@mui/icons-material/FilterList";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Alert,
  Autocomplete,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid2,
  IconButton,
  Paper,
  Popover,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Toolbar,
  Tooltip,
  Typography
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { getAccounts } from "../../api/account";
import { getAdminEntities } from "../../api/entities/getAdminEntities";
import { useErrorHandler } from "../../hooks";
import { AccountEntityPermission } from "../../types/model/EntityPermission/AccountEntityPermission";
import { Account } from "../../types/model/account";
import { Entity } from "../../types/model/entity/Entity";
import GenericFilter, { FilterField } from "../General/GenericFilter";

export interface AccountEntityPermissionsFilters {
  entityId?: number;
  accountId?: number;
}

interface AccountEntityPermissionsTableProps {
  accountEntityPermissionsData: AccountEntityPermission[];
  canEdit: boolean;
  isLoading: boolean;
  onCreate: (values: {
    entityId: number;
    accountId: number;
    permissionType: "read" | "write";
  }) => Promise<void>;
  onDelete: (permission: AccountEntityPermission) => void;
  onRefresh: () => void;
  onApplyFilters: (filters: AccountEntityPermissionsFilters) => void;
}

export default function AccountEntityPermissionsTable({
  accountEntityPermissionsData,
  canEdit,
  isLoading,
  onCreate,
  onDelete,
  onRefresh,
  onApplyFilters
}: Readonly<AccountEntityPermissionsTableProps>) {
  const { t } = useTranslation();
  const handleError = useErrorHandler();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [entities, setEntities] = useState<Entity[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [isOptionsLoading, setIsOptionsLoading] = useState(false);
 
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState<Entity | null>(null);
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const [permissionType, setPermissionType] = useState<"read" | "write">("read");
 
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [filters, setFilters] = useState<AccountEntityPermissionsFilters>({});
  const isFilterOpen = Boolean(filterAnchorEl);

  const handleChangePage = (event: unknown, newPage: number) =>
    setPage(newPage);

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };

  const visibleRows = accountEntityPermissionsData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Load entities and accounts on mount
  useEffect(() => {
    const fetchOptions = async () => {
      setIsOptionsLoading(true);
      try {
        const [entitiesData, accountsData] = await Promise.all([
          getAdminEntities(),
          getAccounts(),
        ]);
        setEntities(entitiesData);
        setAccounts(accountsData);
      } catch (error) {
        handleError(error);
      } finally {
        setIsOptionsLoading(false);
      }
    };
 
    fetchOptions();
  }, []);

  // Add dialog
  const handleOpenAdd = async () => {
    setIsAddOpen(true);
    setIsOptionsLoading(true);
 
    try {
      const [entitiesData, accountsData] = await Promise.all([
        getAdminEntities(),
        getAccounts(),
      ]);
      setEntities(entitiesData);
      setAccounts(accountsData);
    } catch (error) {
      handleError(error);
    } finally {
      setIsOptionsLoading(false);
    }
  };
 
  const handleCloseAdd = () => {
    setIsAddOpen(false);
    setSelectedEntity(null);
    setSelectedAccount(null);
    setPermissionType("read");
  };
 
  const handleConfirmCreate = async () => {
    if (!selectedEntity || !selectedAccount) return;

    setIsCreating(true);
    try {
      await onCreate({
        entityId: selectedEntity.id,
        accountId: selectedAccount.id,
        permissionType,
      });
      handleCloseAdd();
    } catch (error) {
      handleError(error);
    } finally {
      setIsCreating(false);
    }
  };

  // Filters
  const filterFields: FilterField[] = useMemo(
    () => [
      {
        name: "entityId",
        label: t("admin.entity-permissions.entity"),
        type: "autocomplete",
        options: entities.map((entity) => ({
          value: entity.id,
          label: entity.name,
        })),
      },
      {
        name: "accountId",
        label: t("admin.entity-permissions.account"),
        type: "autocomplete",
        options: accounts.map((account) => ({
          value: account.id,
          label: account.name,
        })),
      },
    ],
    [entities, accounts, t]
  );

  const handleToggleFilter = (event: React.MouseEvent<HTMLButtonElement>) => {
    setFilterAnchorEl(filterAnchorEl ? null : event.currentTarget);
  };
 
  const handleCloseFilter = () => {
    setFilterAnchorEl(null);
  };

  const handleDeleteFilterChip = (
    key: keyof AccountEntityPermissionsFilters
  ) => {
    const newFilters = {
      ...filters,
      [key]: undefined,
    };

    setFilters(newFilters);
    onApplyFilters(newFilters);
  };
 
  const activeFilters = useMemo(() => {
    const list: {
      key: keyof AccountEntityPermissionsFilters;
      label: string;
      value: string;
    }[] = [];

    if (filters.entityId) {
      const entity = entities.find(e => e.id === filters.entityId);
      if (entity) {
        list.push({
          key: "entityId",
          label: t("admin.entity-permissions.entity"),
          value: entity.name,
        });
      }
    }

    if (filters.accountId) {
      const account = accounts.find(a => a.id === filters.accountId);
      if (account) {
        list.push({
          key: "accountId",
          label: t("admin.entity-permissions.account"),
          value: account.name,
        });
      }
    }

    return list;
  }, [filters, entities, accounts, t]);

  // Table content
  let tableBodyContent;
 
  if (isLoading) {
    tableBodyContent = (
      <TableRow>
        <TableCell colSpan={4} align="center">
          <CircularProgress />
        </TableCell>
      </TableRow>
    );
  } else if (visibleRows.length === 0) {
    tableBodyContent = (
      <TableRow>
        <TableCell colSpan={4} align="center" sx={{ py: 10 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              opacity: 0.6,
            }}
          >
            <SearchOff sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h6">
              {t("generic.no-records")}
            </Typography>
          </Box>
        </TableCell>
      </TableRow>
    );
  } else {
    tableBodyContent = visibleRows.map((row) => (
      <TableRow key={row.id} hover>
        <TableCell>
          {row.entityName?.toUpperCase()}
        </TableCell>
        <TableCell>
          {row.accountName}
        </TableCell>
        <TableCell>
          <Chip
            label={t(`admin.entity-permissions.permission-type-${row.permissionType}`)}
            size="small"
            color={row.permissionType === "write" ? "primary" : "default"}
            variant="outlined"
          />
        </TableCell>
        <TableCell align="center">
          {canEdit && (
            <Tooltip title={t("generic.delete")}>
              <IconButton
                color="error"
                onClick={(event) => {
                  event.stopPropagation();
                  onDelete(row);
                }}
              >
                <Delete />
              </IconButton>
            </Tooltip>
          )}
        </TableCell>
      </TableRow>
    ));
  }
 
  return (
    <Paper sx={{ width: "100%", mb: 2, p: 2 }}>
      <Toolbar>
        <Typography sx={{ flex: "1 1 100%" }} variant="h4">
          {t("admin.entity-permissions.accounts-title")}
        </Typography>
 
        <Tooltip title={t("generic.filter")}>
          <IconButton onClick={handleToggleFilter}>
            <Badge badgeContent={activeFilters.length} color="primary">
              <FilterListIcon color={isFilterOpen ? "primary" : undefined} />
            </Badge>
          </IconButton>
        </Tooltip>

        <Tooltip title={t("generic.refresh")}>
          <IconButton onClick={onRefresh}>
            <CachedIcon />
          </IconButton>
        </Tooltip>
 
        {canEdit && (
          <Tooltip title={t("generic.add")}>
            <IconButton onClick={handleOpenAdd}>
              <Add />
            </IconButton>
          </Tooltip>
        )}
      </Toolbar>
 
      {activeFilters.length > 0 && (
        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap", gap: 1 }}>
          {activeFilters.map((filter) => (
            <Chip
              key={filter.key}
              label={`${filter.label}: ${filter.value}`}
              onDelete={() => handleDeleteFilterChip(filter.key)}
              size="small"
              variant="outlined"
            />
          ))}
        </Stack>
      )}
 
      <Popover
        open={isFilterOpen}
        anchorEl={filterAnchorEl}
        onClose={handleCloseFilter}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        slotProps={{ paper: { sx: { p: 3, width: 320 } } }}
      >
        <GenericFilter
          fields={filterFields}
          filters={filters}
          onFiltersChange={setFilters}
          onApplyFilters={(newFilters) => {
            setFilters(newFilters as AccountEntityPermissionsFilters);
            onApplyFilters(newFilters as AccountEntityPermissionsFilters);
            setFilterAnchorEl(null);
          }}
          onClearFilters={() => {
            setFilters({});
            onApplyFilters({});
            setFilterAnchorEl(null);
          }}
        />
      </Popover>
 
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t("admin.entity-permissions.entity")}</TableCell>
              <TableCell>{t("admin.entity-permissions.account")}</TableCell>
              <TableCell>{t("admin.entity-permissions.permission-type")}</TableCell>
              <TableCell>{t("generic.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tableBodyContent}
          </TableBody>
        </Table>
      </TableContainer>
 
      <TablePagination
        rowsPerPageOptions={[25, 50, 100]}
        component="div"
        count={accountEntityPermissionsData.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
 
      <Dialog open={isAddOpen} onClose={handleCloseAdd} fullWidth maxWidth="sm">
        <DialogTitle>{t("admin.entity-permissions.add-new")}</DialogTitle>
        <DialogContent>
          <Grid2 container spacing={2} sx={{ mt: 1 }}>
            <Grid2 size={6}>
              <Autocomplete
                options={entities}
                value={selectedEntity}
                onChange={(_, newValue) => setSelectedEntity(newValue)}
                loading={isOptionsLoading}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionKey={(option) => option.id}
                getOptionLabel={(option) => option.name}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <Typography>{option.name}</Typography>

                      <Box sx={{ flexGrow: 1 }} />

                      <Chip
                        label={`ID: ${option.id}`}
                        size="small"
                        sx={{
                          bgcolor: "divider",
                          color: "text.secondary",
                        }}
                      />
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("admin.entity-permissions.entity")}
                    required
                  />
                )}
              />
            </Grid2>
            <Grid2 size={6}>
              <Autocomplete
                options={accounts}
                value={selectedAccount}
                onChange={(_, newValue) => setSelectedAccount(newValue)}
                loading={isOptionsLoading}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionKey={(option) => option.id}
                getOptionLabel={(option) => option.name}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <Typography>{option.name}</Typography>

                      <Box sx={{ flexGrow: 1 }} />

                      <Chip
                        label={`ID: ${option.id}`}
                        size="small"
                        sx={{
                          bgcolor: "divider",
                          color: "text.secondary",
                        }}
                      />
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("admin.entity-permissions.account")}
                    required
                  />
                )}
              />
            </Grid2>
            <Grid2 size={12}>
              <FormControl>
                <FormControlLabel
                  control={
                    <Switch
                      checked={permissionType === "write"}
                      onChange={(_, checked) =>
                        setPermissionType(checked ? "write" : "read")
                      }
                    />
                  }
                  label={
                    permissionType === "write"
                      ? t("admin.entity-permissions.permission-type-write")
                      : t("admin.entity-permissions.permission-type-read")
                  }
                />

                <FormHelperText>
                  {t("admin.entity-permissions.permission-type-account-helper")}
                </FormHelperText>
              </FormControl>
            </Grid2>
            <Alert
              severity="info"
              icon={<InfoOutlinedIcon />}
              sx={{ mt: 2 }}
            >
              {t("admin.entity-permissions.account-permission-info")}
            </Alert>
          </Grid2>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAdd} disabled={isCreating}>
            {t("generic.cancel")}
          </Button>
          <Button
            onClick={handleConfirmCreate}
            variant="contained"
            disabled={isCreating || !selectedEntity || !selectedAccount}
            startIcon={isCreating ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {t("generic.save")}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}